/* eslint-disable prefer-arrow-callback */
import React, { useState } from 'react';
import { findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  CardActions,
  CardContent,
  EyeIcon,
  EyeSlashIcon,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@availity/element';

import { AvailityHeaderLogo, Footer, MessageDisplay } from '@/components';
import { useAppContext } from '@/context';
import { handleSuccessNavigation } from '@/global';
import { useFRCallback } from '@/hooks';

// this page node typically renders for the 'ForgottenPassword002' stage value
const PasswordReset = ({ ...props }) => {
  const { stepToRender, setCurrentStep } = props;
  const { setMessages, queryParams, userHasInvites } = useAppContext();
  const { callbacks = [] } = useFRCallback(stepToRender);
  const [showPassword, setShowPassword] = useState(false);

  const { goto } = queryParams;

  // initialize form
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      password: '',
    },
  });

  const onSubmit = (values) => {
    setMessages([]); // clear out error messages on submit
    const output = { ...stepToRender };

    // TODO: flesh out proper logic
    if (values) {
      const { password } = values;

      const newCallbacks = [...callbacks];
      for (const [index, callbackField] of newCallbacks.entries()) {
        const { input } = callbackField;

        const tokenIndex = findIndex(input, (inputObj) => inputObj.name === 'IDToken2');

        newCallbacks[index].input[tokenIndex] = {
          ...newCallbacks[index]?.input[tokenIndex],
          value: password,
        };
      }

      Object.assign(output.payload.callbacks, [...newCallbacks]);

      setCurrentStep({ ...output });
    }
  };

  return (
    <Grid
      id="enter-current-password-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        size={{ xs: 12, sm: 12, md: 6 }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        sx={{ minHeight: '100vh' }}
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper variant="elevation" elevation={6} sx={{ width: { xs: '350px', sm: '500px', md: '500px' } }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardContent>
                <Typography variant="h1" mb={2}>
                  Reset your password.
                </Typography>
                <Typography variant="subtitle1" mb={3}>
                  In order to secure your account, provide your current password.
                </Typography>
                <Box mb={3}>
                  <TextField
                    id="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    margin="normal"
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            title="password visibility"
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                          >
                            {showPassword ? <EyeIcon fontSize="small" /> : <EyeSlashIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    {...register('password', {
                      required: 'Enter a valid password.',
                    })}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                  />
                </Box>
                <MessageDisplay />
              </CardContent>
              <CardActions sx={{ justifyContent: 'end' }}>
                <Button
                  color="secondary"
                  onClick={() => {
                    handleSuccessNavigation(goto, userHasInvites);
                  }}
                >
                  Back
                </Button>{' '}
                <Button color="primary" type="submit">
                  Continue
                </Button>
              </CardActions>
            </form>
          </Paper>
        </Grid>
        <Footer />
      </Grid>
    </Grid>
  );
};

PasswordReset.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  errorCode: PropTypes.string,
};

export default PasswordReset;
